import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { estampadosService } from "@/services/estampadosService";
import { hojasService } from "@/services/hojasService";
import { categoriasService, Categoria } from "@/services/categoriasService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import {
  Upload,
  Trash2,
  Eye,
  EyeOff,
  ImageIcon,
  X,
  Palette,
  FileText,
  Sparkles,
  Pencil,
  Tags,
  Plus,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const COLOR_OPTIONS = [
  { label: "Gris", value: "bg-gray-100 text-gray-800 border-gray-200" },
  { label: "Azul", value: "bg-blue-100 text-blue-800 border-blue-200" },
  { label: "Rosa", value: "bg-pink-100 text-pink-800 border-pink-200" },
  { label: "Violeta", value: "bg-purple-100 text-purple-800 border-purple-200" },
  { label: "Verde", value: "bg-green-100 text-green-800 border-green-200" },
  { label: "Naranja", value: "bg-orange-100 text-orange-800 border-orange-200" },
  { label: "Amarillo", value: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  { label: "Rojo", value: "bg-red-100 text-red-800 border-red-200" },
];

// ========================================
// COMPONENTE: Uploader de Estampados
// ========================================
const EstampadoUploader = ({
  categoria,
  onUploadSuccess,
}: {
  categoria: Categoria;
  onUploadSuccess: () => void;
}) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nombre, setNombre] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [orientacion, setOrientacion] = useState<"vertical" | "horizontal">("vertical");

  const crearMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !nombre) throw new Error("Faltan datos requeridos");

      const formData = new FormData();
      formData.append("name", nombre);
      formData.append("categoria", categoria.slug);
      formData.append("orientacion", orientacion);
      formData.append("image", selectedFile);

      return estampadosService.crearEstampado(formData);
    },
    onSuccess: () => {
      toast({
        title: "Estampado creado",
        description: `Se agregó "${nombre}" a ${categoria.emoji} ${categoria.label}`,
      });
      setNombre("");
      setSelectedFile(null);
      setPreview(null);
      setOrientacion("vertical");
      onUploadSuccess();
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear estampado",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) procesarArchivo(files[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) procesarArchivo(files[0]);
  };

  const procesarArchivo = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Archivo inválido", description: "Solo imágenes JPG/PNG", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Archivo muy grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    if (!nombre) setNombre(file.name.replace(/\.[^/.]+$/, ""));
  };

  return (
    <Card className="border-2">
      <CardHeader className={`${categoria.color} border-b-2`}>
        <CardTitle className="flex items-center gap-3 text-lg">
          <Sparkles className="h-5 w-5" />
          <div>
            <div className="font-bold">
              Subir Estampado - {categoria.emoji} {categoria.label}
            </div>
            <div className="text-sm font-normal opacity-75">{categoria.descripcion}</div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className={`p-3 rounded-lg border-2 ${categoria.color}`}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <p className="text-sm font-medium">
              Categoría: <strong>{categoria.emoji} {categoria.label}</strong>
            </p>
          </div>
          <p className="text-xs mt-1 opacity-75">
            Este estampado se guardará en la categoría {categoria.slug}
          </p>
        </div>

        <div>
          <Label htmlFor={`nombre-estampado-${categoria.slug}`}>Nombre del Estampado *</Label>
          <Input
            id={`nombre-estampado-${categoria.slug}`}
            placeholder="Ej: Flores Azules"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="mt-1"
          />
        </div>

        {!preview ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
              isDragging ? "border-primary bg-primary/10 scale-105" : "border-gray-300 hover:border-primary/50"
            }`}
          >
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileSelect}
              className="hidden"
              id={`file-estampado-${categoria.slug}`}
            />
            <label
              htmlFor={`file-estampado-${categoria.slug}`}
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="bg-primary/20 rounded-full p-4">
                <Upload className="h-10 w-10 text-brown-dark" />
              </div>
              <p className="text-base font-medium">Arrastra tu imagen aquí</p>
              <p className="text-sm text-brown-light/70">
                o <span className="text-primary font-medium underline">haz click para seleccionar</span>
              </p>
              <p className="text-xs text-brown-light/60">JPG, PNG • Máximo 5MB</p>
            </label>
          </div>
        ) : (
          <div className="relative border-2 border-primary rounded-lg overflow-hidden">
            <img src={preview} alt="Preview" className="w-full max-h-64 object-contain bg-gray-50" />
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { setSelectedFile(null); setPreview(null); }}
              className="absolute top-2 right-2 shadow-lg"
            >
              <X className="h-4 w-4 mr-1" />
              Cambiar
            </Button>
          </div>
        )}

        <div>
          <Label className="text-sm font-medium">Orientación</Label>
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              onClick={() => setOrientacion("vertical")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                orientacion === "vertical" ? "border-primary bg-primary/10 font-medium" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-4 h-6 border-2 border-current rounded-sm" />
              Vertical
            </button>
            <button
              type="button"
              onClick={() => setOrientacion("horizontal")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                orientacion === "horizontal" ? "border-primary bg-primary/10 font-medium" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-6 h-4 border-2 border-current rounded-sm" />
              Horizontal
            </button>
          </div>
        </div>

        <Button
          onClick={() => crearMutation.mutate()}
          disabled={!nombre || !selectedFile || crearMutation.isPending}
          className="w-full bg-brown hover:bg-brown-dark text-white h-12"
          size="lg"
        >
          {crearMutation.isPending ? (
            <><Upload className="h-5 w-5 mr-2 animate-pulse" />Subiendo...</>
          ) : (
            <><Upload className="h-5 w-5 mr-2" />Subir Estampado</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ========================================
// COMPONENTE: Uploader de Hojas Internas
// ========================================
const HojaInternaUploader = ({
  categoria,
  onUploadSuccess,
}: {
  categoria: Categoria;
  onUploadSuccess: () => void;
}) => {
  const { toast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [orden, setOrden] = useState("0");
  const [preview, setPreview] = useState<string | null>(null);
  const [orientacion, setOrientacion] = useState<"vertical" | "horizontal">("vertical");

  const crearMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile || !nombre) throw new Error("Faltan datos requeridos");

      const formData = new FormData();
      formData.append("nombre", nombre);
      formData.append("descripcion", descripcion);
      formData.append("categoria", categoria.slug);
      formData.append("orden", orden);
      formData.append("orientacion", orientacion);
      formData.append("image", selectedFile);

      return hojasService.crearHoja(formData);
    },
    onSuccess: () => {
      toast({
        title: "Hoja creada",
        description: `Se agregó "${nombre}" a ${categoria.emoji} ${categoria.label}`,
      });
      setNombre("");
      setDescripcion("");
      setOrden("0");
      setSelectedFile(null);
      setPreview(null);
      setOrientacion("vertical");
      onUploadSuccess();
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear hoja",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    },
  });

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files[0]) procesarArchivo(files[0]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) procesarArchivo(files[0]);
  };

  const procesarArchivo = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({ title: "Archivo inválido", description: "Solo imágenes JPG/PNG", variant: "destructive" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Archivo muy grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    if (!nombre) setNombre(file.name.replace(/\.[^/.]+$/, ""));
  };

  return (
    <Card className="border-2">
      <CardHeader className={`${categoria.color} border-b-2`}>
        <CardTitle className="flex items-center gap-3 text-lg">
          <FileText className="h-5 w-5" />
          <div>
            <div className="font-bold">
              Subir Hoja Interna - {categoria.emoji} {categoria.label}
            </div>
            <div className="text-sm font-normal opacity-75">{categoria.descripcion}</div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <div className={`p-3 rounded-lg border-2 ${categoria.color}`}>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <p className="text-sm font-medium">
              Categoría: <strong>{categoria.emoji} {categoria.label}</strong>
            </p>
          </div>
          <p className="text-xs mt-1 opacity-75">
            Esta hoja se subirá a la categoría {categoria.slug}
          </p>
        </div>

        <div>
          <Label htmlFor={`nombre-hoja-${categoria.slug}`}>Nombre de la Hoja *</Label>
          <Input
            id={`nombre-hoja-${categoria.slug}`}
            placeholder="Ej: Registro de Vacunas"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor={`descripcion-hoja-${categoria.slug}`}>Descripción (opcional)</Label>
          <Textarea
            id={`descripcion-hoja-${categoria.slug}`}
            placeholder="Breve descripción de esta hoja..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            className="mt-1"
            rows={2}
          />
        </div>

        <div>
          <Label htmlFor={`orden-hoja-${categoria.slug}`}>Orden de visualización</Label>
          <Input
            id={`orden-hoja-${categoria.slug}`}
            type="number"
            min="0"
            placeholder="0"
            value={orden}
            onChange={(e) => setOrden(e.target.value)}
            className="mt-1"
          />
          <p className="text-xs text-brown-light/60 mt-1">Número de orden (menor = aparece primero)</p>
        </div>

        {!preview ? (
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${
              isDragging ? "border-primary bg-primary/10 scale-105" : "border-gray-300 hover:border-primary/50"
            }`}
          >
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleFileSelect}
              className="hidden"
              id={`file-hoja-${categoria.slug}`}
            />
            <label
              htmlFor={`file-hoja-${categoria.slug}`}
              className="cursor-pointer flex flex-col items-center gap-3"
            >
              <div className="bg-primary/20 rounded-full p-4">
                <Upload className="h-10 w-10 text-brown-dark" />
              </div>
              <p className="text-base font-medium">Arrastra tu imagen aquí</p>
              <p className="text-sm text-brown-light/70">
                o <span className="text-primary font-medium underline">haz click para seleccionar</span>
              </p>
              <p className="text-xs text-brown-light/60">JPG, PNG • Máximo 5MB</p>
            </label>
          </div>
        ) : (
          <div className="relative border-2 border-primary rounded-lg overflow-hidden">
            <img src={preview} alt="Preview" className="w-full max-h-64 object-contain bg-gray-50" />
            <Button
              variant="destructive"
              size="sm"
              onClick={() => { setSelectedFile(null); setPreview(null); }}
              className="absolute top-2 right-2 shadow-lg"
            >
              <X className="h-4 w-4 mr-1" />
              Cambiar
            </Button>
          </div>
        )}

        <div>
          <Label className="text-sm font-medium">Orientación</Label>
          <div className="flex gap-2 mt-1">
            <button
              type="button"
              onClick={() => setOrientacion("vertical")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                orientacion === "vertical" ? "border-primary bg-primary/10 font-medium" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-4 h-6 border-2 border-current rounded-sm" />
              Vertical
            </button>
            <button
              type="button"
              onClick={() => setOrientacion("horizontal")}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm ${
                orientacion === "horizontal" ? "border-primary bg-primary/10 font-medium" : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="w-6 h-4 border-2 border-current rounded-sm" />
              Horizontal
            </button>
          </div>
        </div>

        <Button
          onClick={() => crearMutation.mutate()}
          disabled={!nombre || !selectedFile || crearMutation.isPending}
          className="w-full bg-brown hover:bg-brown-dark text-white h-12"
          size="lg"
        >
          {crearMutation.isPending ? (
            <><Upload className="h-5 w-5 mr-2 animate-pulse" />Subiendo...</>
          ) : (
            <><Upload className="h-5 w-5 mr-2" />Subir Hoja</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

// ========================================
// COMPONENTE: Galería de Estampados
// ========================================
const GaleriaEstampados = ({ categoriaSlug }: { categoriaSlug: string }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const { data: estampados, isLoading } = useQuery({
    queryKey: ["estampados", categoriaSlug],
    queryFn: () => estampadosService.getEstampadosPorCategoria(categoriaSlug),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      estampadosService.actualizarEstampado(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estampados", categoriaSlug] });
      toast({ title: "Estado actualizado" });
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => estampadosService.eliminarEstampado(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["estampados", categoriaSlug] });
      setItemToDelete(null);
      toast({ title: "Estampado eliminado" });
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-lg" />
        ))}
      </div>
    );
  }

  if (!estampados || !Array.isArray(estampados) || estampados.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
        <Palette className="h-12 w-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600 font-medium">No hay estampados en esta categoría</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {estampados.map((item) => (
          <div key={item.id} className="relative group">
            <div className={`${item.orientacion === "horizontal" ? "aspect-[4/3]" : "aspect-[3/4]"} rounded-lg overflow-hidden border-2 border-gray-200 bg-white shadow-sm transition-all group-hover:shadow-lg group-hover:scale-105`}>
              <img src={item.path} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <p className="text-sm mt-2 text-center line-clamp-1 font-medium">{item.name}</p>

            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
              <Button
                size="sm"
                variant={item.active ? "default" : "secondary"}
                onClick={() => toggleActiveMutation.mutate({ id: item.id, active: !item.active })}
              >
                {item.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setItemToDelete(item.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {!item.active && (
              <Badge variant="destructive" className="absolute top-2 right-2">Inactivo</Badge>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar estampado?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => itemToDelete && eliminarMutation.mutate(itemToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ========================================
// COMPONENTE: Galería de Hojas Internas
// ========================================
type HojaEditState = {
  id: string;
  nombre: string;
  descripcion: string;
  orden: string;
  orientacion: "vertical" | "horizontal";
};

const GaleriaHojasInternas = ({ categoriaSlug }: { categoriaSlug: string }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [itemToEdit, setItemToEdit] = useState<HojaEditState | null>(null);

  const { data: hojas, isLoading } = useQuery({
    queryKey: ["hojas", categoriaSlug],
    queryFn: () => hojasService.getHojasPorCategoria(categoriaSlug),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      hojasService.actualizarHoja(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hojas", categoriaSlug] });
      toast({ title: "Estado actualizado" });
    },
  });

  const editarMutation = useMutation({
    mutationFn: ({ id, ...data }: HojaEditState) =>
      hojasService.actualizarHoja(id, {
        nombre: data.nombre,
        descripcion: data.descripcion,
        orden: parseInt(data.orden, 10) || 0,
        orientacion: data.orientacion,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hojas", categoriaSlug] });
      setItemToEdit(null);
      toast({ title: "Hoja actualizada" });
    },
    onError: (error: any) => {
      toast({
        title: "Error al actualizar",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => hojasService.eliminarHoja(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hojas", categoriaSlug] });
      setItemToDelete(null);
      toast({ title: "Hoja eliminada" });
    },
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="aspect-square rounded-lg" />
        ))}
      </div>
    );
  }

  if (!hojas || hojas.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
        <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
        <p className="text-gray-600 font-medium">No hay hojas en esta categoría</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {hojas.map((item) => (
          <div key={item.id} className="relative group">
            <div className={`${item.orientacion === "horizontal" ? "aspect-[4/3]" : "aspect-[3/4]"} rounded-lg overflow-hidden border-2 border-gray-200 bg-white shadow-sm transition-all group-hover:shadow-lg group-hover:scale-105`}>
              <img src={item.path} alt={item.nombre} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <p className="text-sm mt-2 text-center line-clamp-1 font-medium">{item.nombre}</p>
            {item.descripcion && (
              <p className="text-xs text-gray-500 text-center line-clamp-1">{item.descripcion}</p>
            )}

            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
              <Button
                size="sm"
                variant={item.active ? "default" : "secondary"}
                onClick={() => toggleActiveMutation.mutate({ id: item.id, active: !item.active })}
              >
                {item.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  setItemToEdit({
                    id: item.id,
                    nombre: item.nombre,
                    descripcion: item.descripcion || "",
                    orden: String(item.orden ?? 0),
                    orientacion: item.orientacion ?? "vertical",
                  })
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setItemToDelete(item.id)}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {!item.active && (
              <Badge variant="destructive" className="absolute top-2 right-2">Inactivo</Badge>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!itemToEdit} onOpenChange={() => setItemToEdit(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar hoja</DialogTitle>
          </DialogHeader>
          {itemToEdit && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Nombre</Label>
                <Input
                  value={itemToEdit.nombre}
                  onChange={(e) => setItemToEdit({ ...itemToEdit, nombre: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Descripción</Label>
                <Textarea
                  value={itemToEdit.descripcion}
                  onChange={(e) => setItemToEdit({ ...itemToEdit, descripcion: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Orden</Label>
                  <Input
                    type="number"
                    value={itemToEdit.orden}
                    onChange={(e) => setItemToEdit({ ...itemToEdit, orden: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Orientación</Label>
                  <Select
                    value={itemToEdit.orientacion}
                    onValueChange={(val: "vertical" | "horizontal") =>
                      setItemToEdit({ ...itemToEdit, orientacion: val })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="vertical">Vertical</SelectItem>
                      <SelectItem value="horizontal">Horizontal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemToEdit(null)}>Cancelar</Button>
            <Button
              onClick={() => itemToEdit && editarMutation.mutate(itemToEdit)}
              disabled={editarMutation.isPending}
            >
              {editarMutation.isPending ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!itemToDelete} onOpenChange={() => setItemToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar hoja?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => itemToDelete && eliminarMutation.mutate(itemToDelete)}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// ========================================
// COMPONENTE: Gestor de Categorías
// ========================================
const GestorCategorias = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [categoriaToDelete, setCategoriaToDelete] = useState<Categoria | null>(null);
  const [form, setForm] = useState({
    tipo: "estampado" as "estampado" | "hoja",
    slug: "",
    label: "",
    emoji: "📁",
    descripcion: "",
    color: COLOR_OPTIONS[0].value,
  });

  const { data: categorias, isLoading } = useQuery({
    queryKey: ["categorias-admin"],
    queryFn: () => categoriasService.getCategoriasAdmin(),
  });

  const crearMutation = useMutation({
    mutationFn: () => categoriasService.crearCategoria(form),
    onSuccess: (nueva) => {
      queryClient.invalidateQueries({ queryKey: ["categorias-admin"] });
      queryClient.invalidateQueries({ queryKey: ["categorias"] });
      toast({ title: "Categoría creada", description: `${nueva.emoji} ${nueva.label} (${nueva.tipo})` });
      setShowForm(false);
      setForm({ tipo: "estampado", slug: "", label: "", emoji: "📁", descripcion: "", color: COLOR_OPTIONS[0].value });
    },
    onError: (error: any) => {
      toast({
        title: "Error al crear categoría",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      categoriasService.actualizarCategoria(id, { active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categorias-admin"] });
      queryClient.invalidateQueries({ queryKey: ["categorias"] });
      toast({ title: "Estado actualizado" });
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => categoriasService.eliminarCategoria(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categorias-admin"] });
      queryClient.invalidateQueries({ queryKey: ["categorias"] });
      setCategoriaToDelete(null);
      toast({ title: "Categoría eliminada" });
    },
    onError: (error: any) => {
      toast({
        title: "No se puede eliminar",
        description: error.response?.data?.error || error.message,
        variant: "destructive",
      });
      setCategoriaToDelete(null);
    },
  });

  const estampados = categorias?.filter((c) => c.tipo === "estampado") ?? [];
  const hojas = categorias?.filter((c) => c.tipo === "hoja") ?? [];

  const slugFromLabel = (label: string) =>
    label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-brown-light text-sm">
          Creá, activá o eliminá categorías para estampados y hojas internas.
        </p>
        <Button onClick={() => setShowForm(!showForm)} className="bg-brown hover:bg-brown-dark text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {/* Formulario de creación */}
      {showForm && (
        <Card className="border-2 border-primary/40">
          <CardHeader>
            <CardTitle className="text-base">Nueva Categoría</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Tipo *</Label>
                <Select
                  value={form.tipo}
                  onValueChange={(val: "estampado" | "hoja") => setForm({ ...form, tipo: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estampado">Estampado (Portada)</SelectItem>
                    <SelectItem value="hoja">Hoja Interna</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Emoji</Label>
                <Input
                  value={form.emoji}
                  onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                  placeholder="📁"
                  maxLength={4}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Nombre (label) *</Label>
              <Input
                value={form.label}
                onChange={(e) => {
                  const label = e.target.value;
                  setForm({ ...form, label, slug: slugFromLabel(label) });
                }}
                placeholder="Ej: Navideño"
              />
            </div>

            <div className="space-y-1">
              <Label>Slug (ID único) *</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s/g, "-") })}
                placeholder="Ej: navideno"
              />
              <p className="text-xs text-gray-500">Se genera automáticamente desde el nombre, pero podés editarlo.</p>
            </div>

            <div className="space-y-1">
              <Label>Descripción (opcional)</Label>
              <Input
                value={form.descripcion}
                onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                placeholder="Ej: Para agendas de fin de año"
              />
            </div>

            <div className="space-y-1">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLOR_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, color: opt.value })}
                    className={`px-3 py-1 rounded-full text-xs border-2 transition-all ${opt.value} ${
                      form.color === opt.value ? "ring-2 ring-offset-1 ring-gray-800 scale-105" : ""
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={() => crearMutation.mutate()}
                disabled={!form.slug || !form.label || crearMutation.isPending}
                className="flex-1 bg-brown hover:bg-brown-dark text-white"
              >
                {crearMutation.isPending ? "Guardando..." : "Crear Categoría"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Lista de categorías */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Categorías de Estampados */}
          <div>
            <h3 className="font-semibold text-brown-dark mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-500" />
              Estampados ({estampados.length})
            </h3>
            <div className="space-y-2">
              {estampados.length === 0 && (
                <p className="text-sm text-gray-500 py-4 text-center border-2 border-dashed rounded-lg">Sin categorías</p>
              )}
              {estampados.map((cat) => (
                <div
                  key={cat.id}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 ${cat.color} ${!cat.active ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{cat.emoji}</span>
                    <div>
                      <p className="font-medium text-sm">{cat.label}</p>
                      <p className="text-xs opacity-70">slug: {cat.slug}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleActiveMutation.mutate({ id: cat.id, active: !cat.active })}
                      title={cat.active ? "Desactivar" : "Activar"}
                    >
                      {cat.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setCategoriaToDelete(cat)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Categorías de Hojas */}
          <div>
            <h3 className="font-semibold text-brown-dark mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4 text-green-500" />
              Hojas Internas ({hojas.length})
            </h3>
            <div className="space-y-2">
              {hojas.length === 0 && (
                <p className="text-sm text-gray-500 py-4 text-center border-2 border-dashed rounded-lg">Sin categorías</p>
              )}
              {hojas.map((cat) => (
                <div
                  key={cat.id}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 ${cat.color} ${!cat.active ? "opacity-50" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{cat.emoji}</span>
                    <div>
                      <p className="font-medium text-sm">{cat.label}</p>
                      <p className="text-xs opacity-70">slug: {cat.slug}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleActiveMutation.mutate({ id: cat.id, active: !cat.active })}
                      title={cat.active ? "Desactivar" : "Activar"}
                    >
                      {cat.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => setCategoriaToDelete(cat)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete */}
      <AlertDialog open={!!categoriaToDelete} onOpenChange={() => setCategoriaToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              {categoriaToDelete && (
                <>
                  Vas a eliminar <strong>{categoriaToDelete.emoji} {categoriaToDelete.label}</strong> ({categoriaToDelete.slug}).
                  <br />
                  Si hay estampados/hojas usando esta categoría, la operación será rechazada.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => categoriaToDelete && eliminarMutation.mutate(categoriaToDelete.id)}
              className="bg-red-600 hover:bg-red-700"
              disabled={eliminarMutation.isPending}
            >
              {eliminarMutation.isPending ? "Eliminando..." : "Eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ========================================
// COMPONENTE PRINCIPAL
// ========================================
export default function AdminEstampadosYHojas() {
  const queryClient = useQueryClient();
  const [seccionActual, setSeccionActual] = useState<"estampados" | "hojas" | "categorias">("estampados");
  const [categoriaEstampado, setCategoriaEstampado] = useState<string>("");
  const [categoriaHoja, setCategoriaHoja] = useState<string>("");

  const { data: categoriasEstampados, isLoading: loadingCatEst } = useQuery({
    queryKey: ["categorias", "estampado"],
    queryFn: () => categoriasService.getCategorias("estampado"),
  });

  const { data: categoriasHojas, isLoading: loadingCatHojas } = useQuery({
    queryKey: ["categorias", "hoja"],
    queryFn: () => categoriasService.getCategorias("hoja"),
  });

  // Setear categoría por defecto cuando cargan
  const catEstDefault = categoriasEstampados?.[0]?.slug ?? "";
  const catHojaDefault = categoriasHojas?.[0]?.slug ?? "";

  const catEstActual = categoriaEstampado || catEstDefault;
  const catHojaActual = categoriaHoja || catHojaDefault;

  const handleUploadSuccess = () => {
    if (seccionActual === "estampados") {
      queryClient.invalidateQueries({ queryKey: ["estampados", catEstActual] });
    } else {
      queryClient.invalidateQueries({ queryKey: ["hojas", catHojaActual] });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-brown-dark font-playfair mb-2">
          Gestión de Estampados y Hojas
        </h1>
        <p className="text-brown-light text-base md:text-lg">
          Administra los diseños de portada, el contenido incluido en tus productos y las categorías.
        </p>
      </div>

      {/* Tabs principales */}
      <Tabs
        value={seccionActual}
        onValueChange={(val) => setSeccionActual(val as "estampados" | "hojas" | "categorias")}
      >
        <TabsList className="grid w-full grid-cols-3 mb-6 h-auto bg-transparent gap-3 p-0">
          <TabsTrigger
            value="estampados"
            className="text-sm md:text-base py-4 flex items-center justify-center gap-2 border-2 data-[state=active]:border-blue-500 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-900 data-[state=active]:shadow-lg data-[state=inactive]:border-gray-300 data-[state=inactive]:bg-white data-[state=inactive]:text-gray-600 hover:border-blue-300 transition-all rounded-lg font-semibold"
          >
            <Sparkles className="h-5 w-5" />
            Estampados (Portadas)
          </TabsTrigger>
          <TabsTrigger
            value="hojas"
            className="text-sm md:text-base py-4 flex items-center justify-center gap-2 border-2 data-[state=active]:border-green-500 data-[state=active]:bg-green-50 data-[state=active]:text-green-900 data-[state=active]:shadow-lg data-[state=inactive]:border-gray-300 data-[state=inactive]:bg-white data-[state=inactive]:text-gray-600 hover:border-green-300 transition-all rounded-lg font-semibold"
          >
            <FileText className="h-5 w-5" />
            Hojas Internas
          </TabsTrigger>
          <TabsTrigger
            value="categorias"
            className="text-sm md:text-base py-4 flex items-center justify-center gap-2 border-2 data-[state=active]:border-orange-500 data-[state=active]:bg-orange-50 data-[state=active]:text-orange-900 data-[state=active]:shadow-lg data-[state=inactive]:border-gray-300 data-[state=inactive]:bg-white data-[state=inactive]:text-gray-600 hover:border-orange-300 transition-all rounded-lg font-semibold"
          >
            <Tags className="h-5 w-5" />
            Categorías
          </TabsTrigger>
        </TabsList>

        {/* Indicador sticky */}
        <div className={`sticky top-0 z-10 mb-6 rounded-lg border-2 p-4 shadow-md transition-all ${
          seccionActual === "estampados" ? "bg-blue-50 border-blue-400"
          : seccionActual === "hojas" ? "bg-green-50 border-green-400"
          : "bg-orange-50 border-orange-400"
        }`}>
          <div className="flex items-center gap-3">
            {seccionActual === "estampados" && (
              <>
                <Sparkles className="h-6 w-6 text-blue-600" />
                <div>
                  <p className="font-bold text-blue-900 text-lg">Estampados de Portada</p>
                  <p className="text-sm text-blue-700">Diseños que el usuario ELIGE al personalizar</p>
                </div>
              </>
            )}
            {seccionActual === "hojas" && (
              <>
                <FileText className="h-6 w-6 text-green-600" />
                <div>
                  <p className="font-bold text-green-900 text-lg">Hojas Internas</p>
                  <p className="text-sm text-green-700">Contenido que VIENE INCLUIDO en el producto</p>
                </div>
              </>
            )}
            {seccionActual === "categorias" && (
              <>
                <Tags className="h-6 w-6 text-orange-600" />
                <div>
                  <p className="font-bold text-orange-900 text-lg">Gestión de Categorías</p>
                  <p className="text-sm text-orange-700">Creá y gestioná categorías dinámicas</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ==================== SECCIÓN: ESTAMPADOS ==================== */}
        <TabsContent value="estampados" className="space-y-6">
          {loadingCatEst ? (
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-28 rounded-lg" />)}
            </div>
          ) : !categoriasEstampados || categoriasEstampados.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
              <Tags className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">No hay categorías de estampados.</p>
              <p className="text-sm text-gray-500 mt-1">Creá una desde la tab "Categorías".</p>
            </div>
          ) : (
            <Tabs value={catEstActual} onValueChange={setCategoriaEstampado}>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
                {categoriasEstampados.map((cat) => (
                  <TabsTrigger
                    key={cat.slug}
                    value={cat.slug}
                    className="py-2 px-4 border-2 data-[state=active]:border-blue-500 data-[state=active]:bg-blue-50 data-[state=inactive]:border-gray-200 data-[state=inactive]:bg-white rounded-lg text-sm font-medium"
                  >
                    {cat.emoji} {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {categoriasEstampados.map((cat) => (
                <TabsContent key={cat.slug} value={cat.slug} className="space-y-6 mt-2">
                  <EstampadoUploader categoria={cat} onUploadSuccess={handleUploadSuccess} />
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Palette className="h-5 w-5" />
                        Estampados - {cat.emoji} {cat.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <GaleriaEstampados categoriaSlug={cat.slug} />
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </TabsContent>

        {/* ==================== SECCIÓN: HOJAS INTERNAS ==================== */}
        <TabsContent value="hojas" className="space-y-6">
          {loadingCatHojas ? (
            <div className="flex gap-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-28 rounded-lg" />)}
            </div>
          ) : !categoriasHojas || categoriasHojas.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
              <Tags className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600 font-medium">No hay categorías de hojas.</p>
              <p className="text-sm text-gray-500 mt-1">Creá una desde la tab "Categorías".</p>
            </div>
          ) : (
            <Tabs value={catHojaActual} onValueChange={setCategoriaHoja}>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
                {categoriasHojas.map((cat) => (
                  <TabsTrigger
                    key={cat.slug}
                    value={cat.slug}
                    className="py-2 px-4 border-2 data-[state=active]:border-green-500 data-[state=active]:bg-green-50 data-[state=inactive]:border-gray-200 data-[state=inactive]:bg-white rounded-lg text-sm font-medium"
                  >
                    {cat.emoji} {cat.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {categoriasHojas.map((cat) => (
                <TabsContent key={cat.slug} value={cat.slug} className="space-y-6 mt-2">
                  <HojaInternaUploader categoria={cat} onUploadSuccess={handleUploadSuccess} />
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Hojas Internas - {cat.emoji} {cat.label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <GaleriaHojasInternas categoriaSlug={cat.slug} />
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </TabsContent>

        {/* ==================== SECCIÓN: CATEGORÍAS ==================== */}
        <TabsContent value="categorias" className="space-y-6">
          <GestorCategorias />
        </TabsContent>
      </Tabs>
    </div>
  );
}
